import React, { ReactElement, useCallback, useContext, useState } from 'react';

import HeaderContainer from '../../components/connectedForm/HeaderContainer';
import HeaderTitle from '../../components/connectedForm/HeaderTitle';
import HeaderAction from '../../components/connectedForm/HeaderAction';
import { InfoButton, SecondaryButton } from '../../components/connectedForm/Button';
import DocumentModal2 from '../../components/modals/DocumentModal2';
import ModalContext from '../../components/Context/ModalContext';
import { IEnumMeta } from '../../graphql/schema/fieldMetaData';
import { ReportOutputType, ReportType } from '../../graphql/schema/p4types';
import { EntityType } from '../../@types/entityTypes';
import BreadCrumbs from '../../components/connectedForm/BreadCrumbs';
import WorkspaceContext from '../../components/Context/WorkspaceContext';
import { formatActionProps } from '../../utils/getActionProps';
import { Dropdown } from '../../components/connectedForm/inputComponents';
import Fieldset from '../../components/connectedForm/Fieldset';
import Legend from '../../components/connectedForm/Legend';
import { useHistory, useLocation } from 'react-router';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { useP4DocumentController } from '../../components/document/DocumentProvider';
import { useActionCriteria } from '../../utils/useActionProps';
import { useSelector } from 'react-redux';
import { IReduxStore } from '../../@types/reduxTypes';
import Acknowledgement from '../../graphql/entities/sales/Acknowledgement';
import ConfirmBulkActionModal from '../../components/modals/ConfirmBulkActionModal';
import UserContext from '../../components/Context/UserContext';
import PickingInstruction from '../../graphql/entities/sales/PickingInstruction';
import ProformaInvoice from '../../graphql/entities/sales/ProformaInvoice';
import QuotationDocument from '../../graphql/entities/sales/QuotationDocument';
import PaymentReceipt from '../../graphql/entities/sales/Payment';

export enum InclusionMethod {
  New = 'NEW',
  Amended = 'AMENDED',
  NewAndAmended = 'NEW&AMENDED',
  Reprint = 'REPRINT',
  View = 'VIEW',
}

export const INCLUSION_METHOD_OPTIONS: Array<{ value: InclusionMethod; label: string }> = [
  {
    value: InclusionMethod.New,
    label: 'New Only',
  },
  {
    value: InclusionMethod.Amended,
    label: 'Amended Only',
  },
  {
    value: InclusionMethod.NewAndAmended,
    label: 'New and Amended',
  },
  {
    value: InclusionMethod.Reprint,
    label: 'Reprint',
  },
];

export const WorkspaceDocumentPrintHeader = ({
  inclusionMethod,
  setInclusionMethod,
  inclusionMethodOptions,
  actionFieldNames,
  actionViewNames,
  entity,
  allowEmail,
  disableGenerateButton,
  children,
  additionalActionProps,
  outputSelection,
  normalizeFields,
  customBreadcrumb,
  onClose,
}: {
  inclusionMethod: InclusionMethod;
  setInclusionMethod: React.Dispatch<React.SetStateAction<InclusionMethod>>;
  inclusionMethodOptions?: Array<{ value: InclusionMethod; label: string }>;
  actionFieldNames: {
    new: string;
    reprint: string;
  };
  actionViewNames: {
    new: string;
    reprint: string;
  };
  entity: EntityType;
  allowEmail?: boolean;
  disableGenerateButton?: boolean;
  children?: any;
  additionalActionProps?: any;
  outputSelection?: Array<{ value: ReportOutputType; label: string }>;
  normalizeFields?: Array<any>; //Normalize these fields out of the action entities.
  customBreadcrumb?: Array<{ path: string; title: string }>;
  onClose?: () => void;
}) => {
  const { selectionState, reloadData } = useContext(WorkspaceContext);
  const history = useHistory();
  const location = useLocation<any>();

  const cancel = () => {
    if (onClose) {
      onClose();
      return;
    }
    const previousUrl = location?.state?.previousLocation;
    if (history && previousUrl) {
      history.goBack();
    }
  };

  const getActionProps = useCallback(() => {
    const formattedActionProps = formatActionProps(
      { ...selectionState, include: !selectionState.selectedAll },
      inclusionMethod !== InclusionMethod.Reprint ? actionFieldNames.new : actionFieldNames.reprint,
      inclusionMethod !== InclusionMethod.Reprint ? actionViewNames.new : actionViewNames.reprint
    );
    const actionProps = normalizeFields
      ? normalizeVariables(formattedActionProps, normalizeFields)
      : formattedActionProps;
    if (additionalActionProps) {
      return { ...actionProps, ...additionalActionProps };
    }
    return actionProps;
  }, [
    actionFieldNames.new,
    actionFieldNames.reprint,
    actionViewNames.new,
    actionViewNames.reprint,
    additionalActionProps,
    inclusionMethod,
    selectionState,
  ]);

  const options = inclusionMethodOptions || INCLUSION_METHOD_OPTIONS;

  return (
    <>
      <DocumentPrintHeader
        inclusion={inclusionMethod}
        getActionProps={getActionProps}
        entity={entity}
        onClose={reloadData}
        onCancel={cancel}
        allowEmail={allowEmail}
        disableGenerateButton={disableGenerateButton}
        outputSelection={outputSelection}
        customBreadcrumb={customBreadcrumb}
      />
      {children}
      <Fieldset cols={1 / 2}>
        <Legend label="Print Details" />
        <Dropdown
          label="Inclusion Method"
          enum={true}
          valueKey="value"
          labelKey="label"
          options={options}
          onChange={setInclusionMethod}
          value={inclusionMethod}
          mandatory={true}
          readOnly={options?.length === 1}
        />
      </Fieldset>
    </>
  );
};

type IDocumentPrintHeaderProps = {
  onCancel?: () => void;
  onClose: () => void;
  getActionProps: () => any;
  inclusion: InclusionMethod;
  allowEmail?: boolean;
  outputSelection?: Array<IEnumMeta<ReportOutputType>>;
  entity: EntityType;
  pageView?: boolean;
  disableGenerateButton?: boolean;
  onComplete?: Function;
  customBreadcrumb?: Array<{ path: string; title: string }>;
};

const useBulkActionClick = (onClick: () => void): [onBulkActionClick: () => void, modal: ReactElement] => {
  const { selectionState } = useContext(WorkspaceContext);
  const {
    currentUser: { bulkActionWarningThreshold },
  } = useContext(UserContext);

  const [toBeClicked, setToBeClicked] = useState<null | (() => void)>(null);

  const onBulkActionClick = useCallback(() => {
    if (selectionState.count >= bulkActionWarningThreshold || selectionState.count === 0) {
      setToBeClicked(() => onClick);
    } else {
      onClick();
    }
  }, [bulkActionWarningThreshold, onClick, selectionState.count]);

  return [
    onBulkActionClick,
    <ConfirmBulkActionModal
      toBeClicked={toBeClicked}
      setToBeClicked={setToBeClicked}
      numItemsEffected={selectionState.count}
    />,
  ];
};

const DocumentPrintHeader = ({
  onCancel,
  onClose,
  getActionProps,
  inclusion,
  allowEmail,
  outputSelection,
  entity,
  pageView,
  disableGenerateButton,
  onComplete,
  customBreadcrumb,
}: IDocumentPrintHeaderProps) => {
  const { reloadData } = useContext(WorkspaceContext);

  const { formTitle, title } = entity;

  let triggerText;
  switch (inclusion) {
    case 'REPRINT':
      triggerText = 'Reprint';
      break;
    case 'VIEW':
      //Null - No generation button for 'View' option.
      triggerText = null;
      break;
    case 'NEW':
    default:
      triggerText = 'Generate';
  }

  const [documentPrintModalOpen, setDocumentPrintModalOpen] = useState(false);
  const { printDocumentActionEntities } = useP4DocumentController();
  const actionCriteria = useActionCriteria();

  const enableDocumentCarousel = useSelector(
    (store: IReduxStore) => store.app.fullUserDetails?.flags?.documentCarouselEnabled ?? false
  );

  let actionContent = (
    <HeaderAction>
      <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
      {!disableGenerateButton && !!triggerText ? (
        <>
          <InfoButton onClick={() => setDocumentPrintModalOpen(true)}>{triggerText}</InfoButton>
          <ModalContext.Provider value={{ onClose: () => setDocumentPrintModalOpen(false) }}>
            <DocumentModal2
              multiValue={true}
              entity={entity}
              overrideActionProps={getActionProps}
              reprint={inclusion === 'REPRINT'}
              outputSelection={outputSelection}
              allowEmail={allowEmail}
              onClose={onClose}
              pageView={pageView}
              useCustomerDefaults={true}
              onComplete={onComplete}
              modalOpen={documentPrintModalOpen}
              modalWrapper={true}
              blockNavigation={true}
            />
          </ModalContext.Provider>
        </>
      ) : null}
    </HeaderAction>
  );

  //TODO - Implement all document types in DocumentProvider so we can remove the 'entity' check.
  const onClickPrint = useCallback(() => {
    printDocumentActionEntities(entity.configuration!.reportType!, actionCriteria, {
      reprint: inclusion === 'REPRINT',
      onPrintComplete: () => {
        reloadData?.();
      },
    });
  }, [actionCriteria, entity.configuration, inclusion, printDocumentActionEntities, reloadData]);
  const [onClickBulkPrint, bulkPrintModal] = useBulkActionClick(onClickPrint);
  if (
    enableDocumentCarousel &&
    (entity === Acknowledgement ||
      entity === PickingInstruction ||
      entity === ProformaInvoice ||
      entity === QuotationDocument ||
      entity === PaymentReceipt)
  ) {
    actionContent = (
      <HeaderAction>
        <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
        {!disableGenerateButton && !!triggerText ? (
          <InfoButton onClick={onClickBulkPrint}>{triggerText}</InfoButton>
        ) : null}
      </HeaderAction>
    );
  }

  return (
    <>
      {bulkPrintModal}
      <BreadCrumbs title={formTitle as string} customBreadcrumb={customBreadcrumb} />
      <HeaderContainer>
        <HeaderTitle title={title} value={formTitle} optValue={null} />
        {actionContent}
      </HeaderContainer>
    </>
  );
};

DocumentPrintHeader.defaultProps = {
  outputSelection: null,
  inclusion: '',
};

export default DocumentPrintHeader;
